/**
 * Project generation settings aligned with veo3-pro workflow constants
 * (`desktop/veo3-pro/src/services/workflow/constants.py` IMAGE_MODEL_MAP / VIDEO_MODEL_MAP).
 *
 * Stored values are workflow map keys so the desktop tool can pass them directly to Flow APIs.
 * Credit labels mix Pro + Ultra tiers from `desktop/labs/python/workflow2/flow.json`.
 */

export type ProjectModelCreditCost = number | 'unavailable';

export interface ProjectTierCredits {
  pro: ProjectModelCreditCost;
  ultra: ProjectModelCreditCost;
}

export interface ProjectGenerationOption {
  label: string;
  credits: ProjectTierCredits;
}

const ZERO_TIER_CREDITS: ProjectTierCredits = { pro: 0, ultra: 0 };

function mapKeys<T extends Record<string, unknown>>(
  map: T,
): [keyof T & string, ...(keyof T & string)[]] {
  return Object.keys(map) as [keyof T & string, ...(keyof T & string)[]];
}

/** Image models — keys match `IMAGE_MODEL_MAP` in workflow constants. */
export const PROJECT_IMAGE_MODELS = {
  nano_banana_pro: {
    label: '\uD83C\uDF4C Nano Banana Pro',
    credits: ZERO_TIER_CREDITS,
  },
  nano_banana_2: {
    label: '\uD83C\uDF4C Nano Banana 2',
    credits: ZERO_TIER_CREDITS,
  },
} as const satisfies Record<string, ProjectGenerationOption>;

/** Video models (I2V) — keys match `VIDEO_MODEL_MAP` in workflow constants. */
export const PROJECT_VIDEO_MODELS = {
  veo_3_1_i2v_lite: {
    label: 'Veo 3.1 - Lite',
    credits: { pro: 10, ultra: 5 },
  },
  veo_3_1_i2v_s_fast: {
    label: 'Veo 3.1 - Fast',
    credits: { pro: 20, ultra: 'unavailable' },
  },
  veo_3_1_i2v_s: {
    label: 'Veo 3.1 - Quality',
    credits: { pro: 100, ultra: 100 },
  },
  abra_i2v_4s: {
    label: 'Omni 1.1 Flash (4s)',
    credits: { pro: 7, ultra: 7 },
  },
  abra_i2v_6s: {
    label: 'Omni 1.1 Flash (6s)',
    credits: { pro: 10, ultra: 10 },
  },
  abra_i2v_8s: {
    label: 'Omni 1.1 Flash (8s)',
    credits: { pro: 12, ultra: 12 },
  },
  abra_i2v_10s: {
    label: 'Omni 1.1 Flash (10s)',
    credits: { pro: 15, ultra: 15 },
  },
} as const satisfies Record<string, ProjectGenerationOption>;

export interface ProjectAspectOption {
  label: string;
}

/**
 * Image aspect keys — match `supportedAspectRatios` in `flow.json` `modelConfig.imageModelFamilies`
 * (stored lowercase snake_case: `landscape` ↔ `LANDSCAPE`, `portrait_3_4` ↔ `PORTRAIT_3_4`, …).
 */
export const PROJECT_IMAGE_ASPECT_RATIOS = {
  landscape: { label: '16:9' },
  portrait: { label: '9:16' },
  square: { label: '1:1' },
  portrait_3_4: { label: '3:4' },
  landscape_4_3: { label: '4:3' },
} as const satisfies Record<string, ProjectAspectOption>;

/**
 * Video aspect keys — match `supportedAspectRatios` in `flow.json` `modelConfig.videoModelFamilies`
 * (`LANDSCAPE`, `PORTRAIT` only).
 */
export const PROJECT_VIDEO_ASPECT_RATIOS = {
  landscape: { label: '16:9' },
  portrait: { label: '9:16' },
} as const satisfies Record<string, ProjectAspectOption>;

export const PROJECT_OUTPUT_COUNT_VALUES = [1, 2, 3, 4] as const;

/** Image upscale — keys match `IMAGE_MODEL_MAP` upscale keys; `default` = no upscale. */
export const PROJECT_IMAGE_DOWNLOAD_QUALITIES = {
  default: { label: 'Default', credits: ZERO_TIER_CREDITS },
  upsample_2k: { label: '2K', credits: ZERO_TIER_CREDITS },
  upsample_4k: { label: '4K', credits: { pro: 'unavailable', ultra: 0 } },
} as const satisfies Record<string, ProjectGenerationOption>;

/** Video upscale — keys match `VIDEO_MODEL_MAP` upsampler keys; `default` = no upscale. */
export const PROJECT_VIDEO_DOWNLOAD_QUALITIES = {
  default: { label: 'Default', credits: ZERO_TIER_CREDITS },
  veo_3_1_upsampler_1080p: { label: '1080p', credits: ZERO_TIER_CREDITS },
  veo_3_1_upsampler_4k: { label: '4K', credits: { pro: 'unavailable', ultra: 50 } },
} as const satisfies Record<string, ProjectGenerationOption>;

export type ProjectImageModel = keyof typeof PROJECT_IMAGE_MODELS;
export type ProjectVideoModel = keyof typeof PROJECT_VIDEO_MODELS;
export type ProjectImageAspectRatio = keyof typeof PROJECT_IMAGE_ASPECT_RATIOS;
export type ProjectVideoAspectRatio = keyof typeof PROJECT_VIDEO_ASPECT_RATIOS;
export type ProjectOutputCount = (typeof PROJECT_OUTPUT_COUNT_VALUES)[number];
export type ProjectImageDownloadQuality = keyof typeof PROJECT_IMAGE_DOWNLOAD_QUALITIES;
export type ProjectVideoDownloadQuality = keyof typeof PROJECT_VIDEO_DOWNLOAD_QUALITIES;

export const PROJECT_IMAGE_MODEL_VALUES = mapKeys(PROJECT_IMAGE_MODELS);
export const PROJECT_VIDEO_MODEL_VALUES = mapKeys(PROJECT_VIDEO_MODELS);
export const PROJECT_IMAGE_ASPECT_RATIO_VALUES = mapKeys(PROJECT_IMAGE_ASPECT_RATIOS);
export const PROJECT_VIDEO_ASPECT_RATIO_VALUES = mapKeys(PROJECT_VIDEO_ASPECT_RATIOS);
export const PROJECT_IMAGE_DOWNLOAD_QUALITY_VALUES = mapKeys(PROJECT_IMAGE_DOWNLOAD_QUALITIES);
export const PROJECT_VIDEO_DOWNLOAD_QUALITY_VALUES = mapKeys(PROJECT_VIDEO_DOWNLOAD_QUALITIES);

export const DEFAULT_PROJECT_IMAGE_MODEL: ProjectImageModel = 'nano_banana_pro';
export const DEFAULT_PROJECT_VIDEO_MODEL: ProjectVideoModel = 'veo_3_1_i2v_lite';

/** Retired CMS option — normalize stored projects to Veo 3.1 Lite. */
const LEGACY_PROJECT_VIDEO_MODEL_ALIASES: Record<string, ProjectVideoModel> = {
  veo_3_1_i2v_lite_low_priority: 'veo_3_1_i2v_lite',
};
export const DEFAULT_PROJECT_IMAGE_ASPECT_RATIO: ProjectImageAspectRatio = 'landscape';
export const DEFAULT_PROJECT_VIDEO_ASPECT_RATIO: ProjectVideoAspectRatio = 'landscape';
export const DEFAULT_PROJECT_OUTPUT_COUNT: ProjectOutputCount = 1;
export const DEFAULT_PROJECT_IMAGE_DOWNLOAD_QUALITY: ProjectImageDownloadQuality = 'default';
export const DEFAULT_PROJECT_VIDEO_DOWNLOAD_QUALITY: ProjectVideoDownloadQuality = 'default';

export function formatMixedTierCredits(credits: ProjectTierCredits): string {
  const { pro, ultra } = credits;

  if (pro === 'unavailable' && ultra === 'unavailable') {
    return ' (Unavailable)';
  }

  if (pro === 'unavailable') {
    if (ultra === 0) return ' (0 credits, Ultra only)';
    const unit = ultra === 1 ? 'credit' : 'credits';
    return ` (${ultra} ${unit}, Ultra only)`;
  }

  if (ultra === 'unavailable') {
    if (pro === 0) return ' (0 credits)';
    const unit = pro === 1 ? 'credit' : 'credits';
    return ` (${pro} ${unit})`;
  }

  if (pro === ultra) {
    const unit = pro === 1 ? 'credit' : 'credits';
    return ` (${pro} ${unit})`;
  }

  const lo = Math.min(pro, ultra);
  const hi = Math.max(pro, ultra);
  return ` (${lo}\u2013${hi} credits)`;
}

function normalizeMapKey<T extends string>(
  value: unknown,
  map: Record<string, unknown>,
  fallback: T,
): T {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed && trimmed in map ? (trimmed as T) : fallback;
}

function generationOptionDisplayLabel<T extends string>(
  map: Record<T, ProjectGenerationOption>,
  value: unknown,
  fallback: T,
): string {
  const key = normalizeMapKey(value, map, fallback);
  const option = map[key];
  return option.label + formatMixedTierCredits(option.credits);
}

function aspectDisplayLabel<T extends string>(
  map: Record<T, ProjectAspectOption>,
  value: unknown,
  fallback: T,
): string {
  return map[normalizeMapKey(value, map, fallback)].label;
}

export function projectImageModelDisplayLabel(value: unknown): string {
  return generationOptionDisplayLabel(PROJECT_IMAGE_MODELS, value, DEFAULT_PROJECT_IMAGE_MODEL);
}

export function projectVideoModelDisplayLabel(value: unknown): string {
  return generationOptionDisplayLabel(PROJECT_VIDEO_MODELS, value, DEFAULT_PROJECT_VIDEO_MODEL);
}

export function projectImageDownloadQualityDisplayLabel(value: unknown): string {
  return generationOptionDisplayLabel(
    PROJECT_IMAGE_DOWNLOAD_QUALITIES,
    value,
    DEFAULT_PROJECT_IMAGE_DOWNLOAD_QUALITY,
  );
}

export function projectVideoDownloadQualityDisplayLabel(value: unknown): string {
  return generationOptionDisplayLabel(
    PROJECT_VIDEO_DOWNLOAD_QUALITIES,
    value,
    DEFAULT_PROJECT_VIDEO_DOWNLOAD_QUALITY,
  );
}

export function projectImageAspectRatioDisplayLabel(value: unknown): string {
  return aspectDisplayLabel(
    PROJECT_IMAGE_ASPECT_RATIOS,
    value,
    DEFAULT_PROJECT_IMAGE_ASPECT_RATIO,
  );
}

export function projectVideoAspectRatioDisplayLabel(value: unknown): string {
  return PROJECT_VIDEO_ASPECT_RATIOS[normalizeProjectVideoAspectRatio(value)].label;
}

export function normalizeProjectImageModel(value: unknown): ProjectImageModel {
  return normalizeMapKey(value, PROJECT_IMAGE_MODELS, DEFAULT_PROJECT_IMAGE_MODEL);
}

export function normalizeProjectVideoModel(value: unknown): ProjectVideoModel {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const legacy = LEGACY_PROJECT_VIDEO_MODEL_ALIASES[trimmed];
    if (legacy) {
      return legacy;
    }
  }
  return normalizeMapKey(value, PROJECT_VIDEO_MODELS, DEFAULT_PROJECT_VIDEO_MODEL);
}

export function normalizeProjectImageAspectRatio(value: unknown): ProjectImageAspectRatio {
  return normalizeMapKey(value, PROJECT_IMAGE_ASPECT_RATIOS, DEFAULT_PROJECT_IMAGE_ASPECT_RATIO);
}

export function normalizeProjectVideoAspectRatio(value: unknown): ProjectVideoAspectRatio {
  if (typeof value !== 'string') return DEFAULT_PROJECT_VIDEO_ASPECT_RATIO;
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_PROJECT_VIDEO_ASPECT_RATIO;
  if (trimmed in PROJECT_VIDEO_ASPECT_RATIOS) return trimmed as ProjectVideoAspectRatio;
  if (trimmed in PROJECT_IMAGE_ASPECT_RATIOS) {
    return trimmed === 'portrait' ? 'portrait' : 'landscape';
  }
  return DEFAULT_PROJECT_VIDEO_ASPECT_RATIO;
}

export function normalizeProjectOutputCount(value: unknown): ProjectOutputCount {
  const fallback = DEFAULT_PROJECT_OUTPUT_COUNT;
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const asInt = Math.trunc(value);
  if (asInt < 1) return 1;
  if (asInt > 4) return 4;
  return asInt as ProjectOutputCount;
}

export function normalizeProjectImageDownloadQuality(value: unknown): ProjectImageDownloadQuality {
  return normalizeMapKey(
    value,
    PROJECT_IMAGE_DOWNLOAD_QUALITIES,
    DEFAULT_PROJECT_IMAGE_DOWNLOAD_QUALITY,
  );
}

export function normalizeProjectVideoDownloadQuality(value: unknown): ProjectVideoDownloadQuality {
  return normalizeMapKey(
    value,
    PROJECT_VIDEO_DOWNLOAD_QUALITIES,
    DEFAULT_PROJECT_VIDEO_DOWNLOAD_QUALITY,
  );
}
